import { Controller } from "@hotwired/stimulus";
import "./component.scss";

// Temporarily presents the existing fields, retaining their form and item ownership.
export default class extends Controller {
  static targets = ["title", "body", "footer"];

  open({ title, owner, content = [], groups = [], actions = [], apply, cancel, closeAll, returnFocus, showApply = true }) {
    if (this.element.open || this.closing) return;
    this.origin = this.element.parentElement;
    this.returnFocus = returnFocus || document.activeElement;
    this.onApply = apply;
    this.onCancel = cancel;
    this.onCloseAll = closeAll;
    this.footerTarget.hidden = !showApply;
    const groupedContent = groups.flatMap(group => group.content || []);
    this.borrowed = [ ...content, ...groupedContent ].map((node) => {
      const marker = document.createComment("mobile-field-position");
      node.before(marker);
      return { node, marker, hidden: node.hidden };
    });
    owner.append(this.element);
    this.titleTarget.textContent = title;
    this.borrowed.forEach(({ node }) => { node.hidden = false; this.bodyTarget.append(node); });
    groups.forEach(({ label, content: groupContent = [], actions: groupActions = [], className = "" }) => {
      const section = document.createElement("section");
      section.className = [ "lib-editor__mobile-settings-group", className ].filter(Boolean).join(" ");
      if (label) {
        const heading = document.createElement("h3");
        heading.textContent = label;
        section.append(heading);
      }
      const groupBody = document.createElement("div");
      groupBody.className = "lib-editor__mobile-settings-group-content";
      groupBody.append(...groupContent, ...groupActions.map(action => this.actionButton(action)));
      section.append(groupBody);
      this.bodyTarget.append(section);
    });
    actions.forEach(action => this.bodyTarget.append(this.actionButton(action)));
    this.element.showModal();
    this.titleTarget.focus({ preventScroll: true });
    this.motion([{ transform: "translateY(40px)", opacity: 0 }, { transform: "translateY(0)", opacity: 1 }], 250);
  }

  actionButton({ label, icon, run, disabled, danger }) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `button button--${danger ? "warning" : "secondary"}`;
    button.disabled = Boolean(disabled);
    if (icon) button.append(icon.cloneNode(true));
    button.append(document.createTextNode(label));
    button.addEventListener("click", run);
    return button;
  }

  async apply() {
    if (this.onApply?.() === false) return;
    await this.close();
  }

  async cancel(event) {
    event?.preventDefault();
    await this.close();
    this.onCancel?.();
  }

  back(event) {
    return this.cancel(event);
  }

  async closeAll(event) {
    event?.preventDefault();
    await this.close({ restoreFocus: false });
    this.onCancel?.();
    await this.onCloseAll?.();
  }

  async close({ restoreFocus = true } = {}) {
    if (this.closing || !this.element.open) return;
    this.closing = true;
    this.restoreFocusOnClose = restoreFocus;
    await this.motion([{ transform: "translateY(0)", opacity: 1 }, { transform: "translateY(24px)", opacity: 0 }], 150);
    this.element.close();
    this.restore();
    this.closing = false;
  }

  restore() {
    if (this.element.open || !this.origin) return;
    this.borrowed.forEach(({ node, marker, hidden }) => {
      node.hidden = hidden;
      marker.replaceWith(node);
    });
    this.bodyTarget.replaceChildren();
    this.footerTarget.hidden = false;
    this.origin.append(this.element);
    this.origin = null;
    const returnFocusAvailable = this.returnFocus?.isConnected && !this.returnFocus.hidden && !this.returnFocus.closest("[hidden]");
    const focus = returnFocusAvailable ? this.returnFocus
      : [...this.element.closest("form").querySelectorAll("[data-mobile-focus]")].find(node => node.dataset.mobileFocus === this.returnFocus?.dataset.mobileFocus);
    const parentDialog = [...document.querySelectorAll("dialog[open]")].at(-1);
    if (this.restoreFocusOnClose !== false) {
      (focus || parentDialog?.querySelector("[tabindex='-1'], button") || this.element.closest("form").querySelector(".lib-editor__header button"))?.focus({ preventScroll: true });
    }
    this.restoreFocusOnClose = true;
  }

  motion(frames, duration) {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return Promise.resolve();
    return this.element.animate(frames, { duration, easing: "cubic-bezier(.22,1,.36,1)" }).finished.catch(() => {});
  }
}
